import React from "react";
import { useTranslation } from "react-i18next";

interface SkeletonProps {
  /** Sizing/spacing utilities (e.g. "h-4 w-32"). Color and pulse come built in. */
  className?: string;
}

/** One pulsing placeholder block, shaped by the caller's sizing classes. */
export const Skeleton: React.FC<SkeletonProps> = ({ className = "" }) => (
  <div
    aria-hidden="true"
    className={`animate-pulse rounded-md bg-grey-90 dark:bg-primary-20 ${className}`}
  />
);

interface ListRowsSkeletonProps {
  /** How many placeholder rows to render. */
  rows?: number;
}

/**
 * A stack of card-shaped placeholder rows — the loading stand-in for the
 * session / member card lists, matching their spacing so content doesn't jump.
 */
export const ListRowsSkeleton: React.FC<ListRowsSkeletonProps> = ({ rows = 3 }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-4" role="status" aria-label={t("common.loading")}>
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 rounded-2xl border border-grey-90 p-4 dark:border-primary-20"
        >
          <Skeleton className="h-12 w-12 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="hidden h-8 w-20 md:block" />
        </div>
      ))}
    </div>
  );
};

interface TableRowsSkeletonProps {
  /** How many placeholder lines to render. */
  rows?: number;
}

/** Slim full-width placeholder lines — the loading stand-in for table bodies. */
export const TableRowsSkeleton: React.FC<TableRowsSkeletonProps> = ({ rows = 5 }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-2" role="status" aria-label={t("common.loading")}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
};

export default Skeleton;
